import { Slot } from "./Slot";
import { cn } from "@/lib/cn";
import type { ButtonHTMLAttributes, ReactNode } from "react";

type Variant = "primary" | "secondary" | "ghost" | "outline" | "danger";
type Size = "sm" | "md" | "lg";

const base =
  "inline-flex items-center justify-center gap-2 font-medium rounded-full transition-colors " +
  "disabled:opacity-50 disabled:pointer-events-none select-none whitespace-nowrap " +
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-forest";

const variants: Record<Variant, string> = {
  // Verde profundo = acción principal / confianza
  primary: "bg-forest text-bone-50 hover:bg-forest-strong",
  // Arena para acciones secundarias, sobre fondo hueso
  secondary: "bg-sand-200 text-ink hover:bg-sand-300",
  ghost: "text-ink-soft hover:bg-sand-100",
  outline: "border border-sand-300 text-ink hover:bg-sand-100 bg-transparent",
  // Terracota oscura para destructivo / seguridad crítica (no rojo neón)
  danger: "bg-danger text-bone-50 hover:brightness-95",
};

const sizes: Record<Size, string> = {
  sm: "h-9 px-4 text-sm",
  md: "h-11 px-5 text-[0.95rem]",
  lg: "h-13 px-7 text-base min-h-[3.25rem]",
};

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  /** Ocupa todo el ancho — patrón mobile-first para CTAs al pie. */
  block?: boolean;
  /** Renderiza como el hijo (ej. envolver un <Link>) en vez de <button>. */
  asChild?: boolean;
  children?: ReactNode;
}

export function Button({
  variant = "primary",
  size = "md",
  block = false,
  asChild = false,
  className,
  children,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      className={cn(base, variants[variant], sizes[size], block && "w-full", className)}
      {...props}
    >
      {children}
    </Comp>
  );
}
